import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './ProductPatternPage.css';

const SeedIcon = () => (
  <svg viewBox="0 0 80 80" className="product-placeholder__icon" aria-hidden="true">
    <defs>
      <linearGradient id="productSeedGlow" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
        <stop offset="100%" stopColor="#d7f3c6" stopOpacity="0.75" />
      </linearGradient>
    </defs>
    <circle cx="40" cy="40" r="30" fill="url(#productSeedGlow)" opacity="0.95" />
    <path
      d="M40 20c8 5 14 13 14 21 0 10-6 19-14 19s-14-9-14-19c0-8 6-16 14-21Z"
      fill="#2d6e2d"
    />
    <path d="M40 26v28" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" />
    <path d="M40 37c-4-2-7-5-10-8" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M40 42c4-2 7-5 10-8" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

export default function ProductPatternPage({
  title,
  categories = null,
  varieties = [],
  pageClass = '',
  heroImage = null,
  heroLogo = null,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const hasCategories = Array.isArray(categories) && categories.length > 0;
  const [activeTab, setActiveTab] = useState(hasCategories ? categories[0].key : 'all');
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  const [shareStatus, setShareStatus] = useState('');
  const relatedSliderRef = useRef(null);

  const getVarietySlug = (value) =>
    String(value || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const pathParts = location.pathname.split('/').filter(Boolean);
  const mainProductSlug = pathParts[1] || '';
  const varietySlugFromRoute = pathParts[2] || '';

  const activeCategory = useMemo(() => {
    if (!hasCategories) {
      return { label: 'All Varieties', varieties };
    }

    const selectedCategory = categories.find((category) => category.key === activeTab) || categories[0];

    return {
      ...selectedCategory,
      varieties: selectedCategory.varieties,
    };
  }, [activeTab, categories, hasCategories, varieties]);

  const selectedVariety = useMemo(
    () => activeCategory.varieties.find((item) => getVarietySlug(item.name) === varietySlugFromRoute),
    [activeCategory.varieties, varietySlugFromRoute]
  );

  const relatedVarieties = useMemo(
    () => activeCategory.varieties.filter((item) => getVarietySlug(item.name) !== varietySlugFromRoute),
    [activeCategory.varieties, varietySlugFromRoute]
  );

  const detailCarouselImages = useMemo(
    () => (Array.isArray(selectedVariety?.carouselImages) ? selectedVariety.carouselImages.filter(Boolean) : []),
    [selectedVariety]
  );
  const detailMediaImages = useMemo(() => {
    if (!selectedVariety) {
      return [];
    }

    const allImages = [selectedVariety.image, ...detailCarouselImages].filter(Boolean);
    return allImages.filter((imageSrc, index) => allImages.indexOf(imageSrc) === index);
  }, [detailCarouselImages, selectedVariety]);
  const [activeDetailImageIndex, setActiveDetailImageIndex] = useState(0);

  const showDetailView = Boolean(varietySlugFromRoute && selectedVariety);

  useEffect(() => {
    setActiveDetailImageIndex(0);
  }, [selectedVariety?.name, varietySlugFromRoute]);

  // Update Open Graph meta tags when product details change
  useEffect(() => {
    if (!showDetailView || !selectedVariety) {
      return;
    }

    const slug = varietySlugFromRoute || getVarietySlug(selectedVariety?.name || title);
    const shareUrl = `${window.location.origin}/products/${mainProductSlug}/${slug}`;
    
    // Create comprehensive description from multiple points
    const descriptionPoints = selectedVariety.description?.slice(0, 4) || [];
    const fullDescription = descriptionPoints.length > 0 
      ? descriptionPoints.join('\n• ') 
      : `Learn more about ${selectedVariety.name}`;
    
    const imageUrl = selectedVariety.image || heroImage || '';

    // Update or create Open Graph meta tags
    const updateMetaTag = (property, content) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    const updateMetaNameTag = (name, content) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    // Set Open Graph meta tags
    updateMetaTag('og:title', `${selectedVariety.name} - Dynasty Agri Seeds`);
    updateMetaTag('og:description', fullDescription);
    updateMetaTag('og:image', imageUrl);
    updateMetaTag('og:url', shareUrl);
    updateMetaTag('og:type', 'website');
    
    // Set Twitter card meta tags
    updateMetaNameTag('twitter:card', 'summary_large_image');
    updateMetaNameTag('twitter:title', `${selectedVariety.name} - Dynasty Agri Seeds`);
    updateMetaNameTag('twitter:description', fullDescription);
    updateMetaNameTag('twitter:image', imageUrl);
    
    // Set standard meta description
    updateMetaNameTag('description', fullDescription);

    // Update document title
    document.title = `${selectedVariety.name} - Dynasty Agri Seeds`;
  }, [showDetailView, selectedVariety, mainProductSlug, varietySlugFromRoute, title, heroImage]);

  const showDetailMediaCarousel = detailMediaImages.length > 1;
  const activeDetailImage = detailMediaImages[activeDetailImageIndex] || selectedVariety?.image;

  const handleDetailImageStep = (direction) => {
    if (!showDetailMediaCarousel) {
      return;
    }

    setActiveDetailImageIndex((prev) => (prev + direction + detailMediaImages.length) % detailMediaImages.length);
  };

  const handleTabSelect = (key) => {
    setActiveTab(key);
    setMobileCategoriesOpen(false);

    if (varietySlugFromRoute) {
      navigate(`/products/${mainProductSlug}`);
    }
  };

  const handleShare = async () => {
    const slug = varietySlugFromRoute || getVarietySlug(selectedVariety?.name || title);
    
    // Create comprehensive description from multiple points
    const descriptionPoints = selectedVariety?.description?.slice(0, 4) || [];
    const fullDescription = descriptionPoints.length > 0 
      ? descriptionPoints.join('\n• ') 
      : `Learn more about ${selectedVariety?.name || title}`;
    
    // Build clean share URL
    const shareUrl = `${window.location.origin}/products/${mainProductSlug}/${slug}`;
    
    // Enhanced share data object
    const shareData = {
      title: selectedVariety ? `${selectedVariety.name} - Dynasty Agri Seeds` : title,
      text: fullDescription,
      url: shareUrl,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        setShareStatus('Shared successfully');
        return;
      }

      // Fallback: copy URL to clipboard
      await navigator.clipboard.writeText(shareUrl);
      setShareStatus('Link copied');
    } catch (error) {
      setShareStatus('Unable to share right now');
    }

    window.setTimeout(() => setShareStatus(''), 2000);
  };

  const scrollRelated = (direction) => {
    if (!relatedSliderRef.current) {
      return;
    }

    const slider = relatedSliderRef.current;
    const scrollStep = Math.max(280, Math.round(slider.clientWidth * 0.9));
    slider.scrollBy({ left: direction * scrollStep, behavior: 'smooth' });
  };

  return (
    <div className={`product-page ${pageClass}`.trim()}>
      {!varietySlugFromRoute && (
        <>
          <section
            className="product-hero"
            style={
              heroImage
                ? {
                    backgroundImage: `url(${heroImage})`,
                    backgroundSize: '100% 100%',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }
                : undefined
            }
          >
            <div className="product-hero__overlay" />
            {heroLogo ? (
              <div className="product-hero__content product-hero__content--logo">
                <img src={heroLogo} alt={`${title} logo`} className="product-hero__logo" loading="lazy" />
              </div>
            ) : null}
          </section>

          <div className="product-simple-heading-wrap">
            <h2>{title} PRODUCTS</h2>
            <div className="products-intro-text" style={{ marginTop: '0.5rem' }}>
              <p>Discover our premium selection of {title} hybrids. Carefully bred for superior yield, exceptional disease resistance, and consistent market quality, our genetics empower farmers with reliable, high-performance crops season after season.</p>
            </div>
          </div>
        </>
      )}

      {hasCategories && (
        <>
          <section className="product-tabs-wrap">
            <div className="product-tabs" role="tablist" aria-label={`${title} categories`}>
              {categories.map((category) => {
                const isActive = activeTab === category.key;

                return (
                  <button
                    key={category.key}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={`product-tab${isActive ? ' product-tab--active' : ''}`}
                    onClick={() => handleTabSelect(category.key)}
                  >
                    {category.label}
                  </button>
                );
              })}
            </div>
          </section>

          <section className="product-mobile-selector">
            <button
              type="button"
              className="product-mobile-selector__button"
              onClick={() => setMobileCategoriesOpen((prev) => !prev)}
              aria-expanded={mobileCategoriesOpen}
              aria-controls="product-mobile-categories"
            >
              <span className="product-mobile-selector__title">Categories</span>
              <span className="product-mobile-selector__current">{activeCategory.label}</span>
              <span className={`product-mobile-selector__caret${mobileCategoriesOpen ? ' open' : ''}`}>
                ▾
              </span>
            </button>

            {mobileCategoriesOpen && (
              <div className="product-mobile-selector__dropdown" id="product-mobile-categories">
                {categories.map((category) => {
                  const isActive = activeTab === category.key;

                  return (
                    <button
                      key={category.key}
                      type="button"
                      className={`product-mobile-selector__item${isActive ? ' product-mobile-selector__item--active' : ''}`}
                      onClick={() => handleTabSelect(category.key)}
                    >
                      <span>{category.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}

      <section className="product-content">
        {showDetailView && (
          <nav className="product-breadcrumb" aria-label="Breadcrumb">
            <Link to="/products">Products</Link>
            <span aria-hidden="true">/</span>
            <Link to={`/products/${mainProductSlug}`}>{title}</Link>
            <span aria-hidden="true">/</span>
            <span>{selectedVariety.name}</span>
          </nav>
        )}

        {!varietySlugFromRoute && (
          <div className="product-pattern-grid product-pattern-grid--catalog">
            {activeCategory.varieties.map((variety) => (
              <Link
                className="product-pattern-card product-pattern-card--catalog"
                key={variety.name}
                to={`/products/${mainProductSlug}/${getVarietySlug(variety.name)}`}
              >
                <div className="product-pattern-card__image">
                  {variety.image ? (
                    <img src={variety.image} alt={variety.name} className="product-pattern-media-image" loading="lazy" />
                  ) : (
                    <SeedIcon />
                  )}
                </div>

                <div className="product-pattern-card__body product-pattern-card__body--compact">
                  <h3>{variety.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        )}

        {showDetailView && (
          <article className="product-pattern-detail-card">
            <div className="product-pattern-detail-card__media">
              <button
                type="button"
                className="product-pattern-share-btn"
                onClick={handleShare}
                aria-label="Share this product"
                title="Share"
              >
                <span className="material-symbols-outlined product-pattern-share-icon" aria-hidden="true">
                  share
                </span>
              </button>

              {activeDetailImage ? (
                <img
                  src={activeDetailImage}
                  alt={selectedVariety.name}
                  className="product-pattern-media-image product-pattern-media-image--detail"
                />
              ) : (
                <SeedIcon />
              )}

              {showDetailMediaCarousel && (
                <>
                  <button
                    type="button"
                    className="product-pattern-detail-card__media-nav product-pattern-detail-card__media-nav--prev"
                    onClick={() => handleDetailImageStep(-1)}
                    aria-label="Previous image"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    className="product-pattern-detail-card__media-nav product-pattern-detail-card__media-nav--next"
                    onClick={() => handleDetailImageStep(1)}
                    aria-label="Next image"
                  >
                    ›
                  </button>

                  <div className="product-pattern-detail-card__media-dots" role="tablist" aria-label="Product images">
                    {detailMediaImages.map((_, index) => {
                      const isActive = index === activeDetailImageIndex;
                      return (
                        <button
                          type="button"
                          key={`${selectedVariety.name}-image-dot-${index}`}
                          className={`product-pattern-detail-card__media-dot${isActive ? ' active' : ''}`}
                          onClick={() => setActiveDetailImageIndex(index)}
                          aria-label={`Show image ${index + 1}`}
                          aria-selected={isActive}
                          role="tab"
                        />
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            <div className="product-pattern-detail-card__body">
              <h3>{selectedVariety.name}</h3>
              <ul className="product-points product-points--detail">
                {selectedVariety.description.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
              {shareStatus ? <p className="product-pattern-share-status">{shareStatus}</p> : null}
            </div>

            {relatedVarieties.length > 0 && (
              <section className="product-pattern-related-products">
                <div className="product-pattern-related-products__header">
                  <h4>More in {title}</h4>
                  <div className="product-pattern-related-products__actions">
                    <button type="button" onClick={() => scrollRelated(-1)} aria-label="Scroll related products left">
                      ‹
                    </button>
                    <button type="button" onClick={() => scrollRelated(1)} aria-label="Scroll related products right">
                      ›
                    </button>
                  </div>
                </div>

                <div className="product-pattern-related-products__track" ref={relatedSliderRef}>
                  {relatedVarieties.map((item) => (
                    <button
                      type="button"
                      key={item.name}
                      className="product-pattern-related-card"
                      onClick={() => navigate(`/products/${mainProductSlug}/${getVarietySlug(item.name)}`)}
                    >
                      <div className="product-pattern-related-card__image">
                        {item.image ? <img src={item.image} alt={item.name} className="product-pattern-media-image" loading="lazy" /> : <SeedIcon />}
                      </div>
                      <h5 className="product-pattern-related-card__name">{item.name}</h5>
                    </button>
                  ))}
                </div>
              </section>
            )}
          </article>
        )}

        {varietySlugFromRoute && !selectedVariety && (
          <div className="product-empty-state">
            <p>The selected variety could not be found.</p>
            <Link to={`/products/${mainProductSlug}`} className="product-back-link">
              Back to {title} varieties
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}
